import { Button } from "@bednbody/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@bednbody/ui/components/card";
import { cn } from "@bednbody/ui/lib/utils";
import { CaretDownIcon } from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { type ReactNode, useState } from "react";
import { Link } from "react-router-dom";

const STORAGE_PREFIX = "bednbody-ops-section:";

function loadCollapsed(id: string): boolean {
  try {
    return localStorage.getItem(STORAGE_PREFIX + id) === "1";
  } catch {
    return false;
  }
}

interface CollapsibleSectionProps {
  /** Stable id — the collapse state persists in localStorage under it. */
  id: string;
  title: string;
  icon: Icon;
  children: ReactNode;
  badge?: string;
  badgeTone?: "warning" | "muted";
  viewAllHref?: string;
  className?: string;
}

/** SectionCard variant whose body collapses, remembered per user/browser. */
export function CollapsibleSection({
  id,
  title,
  icon: IconComp,
  children,
  badge,
  badgeTone = "warning",
  viewAllHref,
  className,
}: CollapsibleSectionProps) {
  const [collapsed, setCollapsed] = useState(() => loadCollapsed(id));

  const toggle = () => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(STORAGE_PREFIX + id, c ? "0" : "1");
      } catch {
        // private mode — collapse just won't persist
      }
      return !c;
    });
  };

  return (
    <Card className={cn("rounded-2xl shadow-xs", className)}>
      <CardHeader>
        <CardTitle className="flex min-w-0 items-center gap-2">
          <IconComp size={16} className="shrink-0 text-primary" />
          <h2 className="truncate text-sm font-semibold text-foreground">{title}</h2>
          {badge ? (
            <span
              className={cn(
                "shrink-0 rounded-full px-2 py-0.5 text-xs font-medium",
                badgeTone === "warning"
                  ? "bg-warning/15 text-warning"
                  : "bg-muted text-muted-foreground",
              )}
            >
              {badge}
            </span>
          ) : null}
        </CardTitle>
        <div className="flex items-center gap-2">
          {viewAllHref ? (
            <Link to={viewAllHref} className="text-xs font-medium text-primary hover:underline">
              View all
            </Link>
          ) : null}
          <Button
            variant="ghost"
            size="icon-sm"
            type="button"
            onClick={toggle}
            aria-expanded={!collapsed}
            aria-label={collapsed ? `Expand ${title}` : `Collapse ${title}`}
          >
            <CaretDownIcon className={cn("transition-transform", collapsed && "-rotate-90")} />
          </Button>
        </div>
      </CardHeader>
      {!collapsed ? <CardContent>{children}</CardContent> : null}
    </Card>
  );
}
